var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { Store } from '@ngxs/store';
import { SetIncomingStory, LoadStory, ReorderLoadChunks } from '../story/state/story.actions';
import { EngineState } from '../engine/state/engine.state';
import { StoryState } from '../story/state/story.state';
import { combineLatest } from 'rxjs';
import { filter, take, delay } from 'rxjs/operators';
import { SelectChapter, PreviousChapter, NextChapter, RecordChapterVisibilityState } from '../story/state/chapters/story.chapters.actions';
import { ToggleVisibleNodes, SetCheckedNodes, CheckNode, SelectNode, ExpandNode, Isolate, QueryNodeInfo, PaintNodes } from '../engine/state/nodes/engine.nodes.actions';
import { EngineNodesState } from '../engine/state/nodes/engine.nodes.state';
import { ToolEnum } from '../engine/models/tool.enum';
import { SetActiveTool, PlayAnimation, ToggleLabels, FlyToObject, SetGraph } from '../engine/state/engine.actions';
import { ViewState } from './state/view.state';
import { ViewLayoutStatus } from './models/view-layout-status.enum';
import { ViewActions } from './state/view.actions';
import { PaintPresetFade } from '../engine/models/paint-presets.const';
import { SelectLabel } from '../story/state/labels/story.labels.actions';
import { StoryChaptersState } from '../story/state/chapters/story.chapters.state';
import { CopyChapterSynchronization, ClearHistory, CacheChapterSynchronization, Undo } from '../history/state/history.actions';
import { HistoryService } from '../history/history.service';
import { SetCamera } from '../engine/state/camera/engine.camera.actions';
import { ViewBreakpoint } from './models/view-breakpoint.enum';
import { Location } from '@angular/common';
import { ViewMode } from './models/view-mode.enum';
import { normalizeQueryIdParam, normalizeQueryTypeParam } from '../widget-config/initial-config';
import { EngineService } from '../engine/engine.service';
import * as i0 from "@angular/core";
import * as i1 from "@ngxs/store";
import * as i2 from "../history/history.service";
import * as i3 from "@angular/common";
import * as i4 from "../engine/engine.service";
var ViewService = /** @class */ (function () {
    function ViewService(store, historyService, location, engineService) {
        this.store = store;
        this.historyService = historyService;
        this.location = location;
        this.engineService = engineService;
    }
    // ---------- Story Handlers ----------
    ViewService.prototype.initStory = function (id, type, lang) {
        var _this = this;
        // Initialize state for new story
        this.store.dispatch(new SetIncomingStory(id, type, true));
        // this.store.dispatch(new SelectBookmark(id, type));
        // Set up listeners for when the story is loaded
        var engineReady$ = this.store.select(EngineState.getReady);
        var incoming$ = this.store.select(StoryState.getIncomingStoryInfo);
        combineLatest(engineReady$, incoming$).pipe(filter(function (_a) {
            var _b = __read(_a, 2), ready = _b[0], incoming = _b[1];
            return ready && !!incoming.id;
        }), take(1)).subscribe(function () {
            _this.store.dispatch(new SelectChapter()); // null chapter state
            _this.store.dispatch(new LoadStory(id, type, lang));
        });
    };
    ViewService.prototype.nextChapter = function () {
        this.store.dispatch(new NextChapter());
    };
    ViewService.prototype.previousChapter = function () {
        this.store.dispatch(new PreviousChapter());
    };
    ViewService.prototype.selectChapter = function (chapter) {
        this.store.dispatch([
            new ReorderLoadChunks(chapter.id),
            new SelectChapter(chapter)
        ]);
    };
    // ---------- 3D Handlers ----------
    ViewService.prototype.hideSelectedStructures = function () {
        var selectedNodes = this.store.selectSnapshot(EngineNodesState.getCheckedNodes);
        this.store.dispatch(new ToggleVisibleNodes(selectedNodes, false));
    };
    // ---------- UI Handlers ----------
    ViewService.prototype.selectTool = function (tool) {
        this.store.dispatch(new SetActiveTool(tool));
    };
    ViewService.prototype.toggleColumn = function (show) {
        var layout = this.store.selectSnapshot(ViewState.getLayout);
        show = show == null ? !(layout.column.status === ViewLayoutStatus.Show) : show;
        layout.column.status = show ? ViewLayoutStatus.Show : ViewLayoutStatus.Collapse;
        this.store.dispatch(new ViewActions.SetLayout(layout));
    };
    ViewService.prototype.togglePresentation = function () {
        var isPresenting = this.store.selectSnapshot(ViewState.getPresentationActive);
        (isPresenting) ? this.stopPresentation() : this.startPresentation();
    };
    ViewService.prototype.startPresentation = function () {
        this.store.dispatch(new ViewActions.TogglePresentation(true));
    };
    ViewService.prototype.stopPresentation = function () {
        this.store.dispatch(new ViewActions.TogglePresentation(false));
    };
    ViewService.prototype.toggleHelp = function (show) {
        show = (show == null) ? !this.store.selectSnapshot(ViewState.getShowHelp) : show;
        this.store.dispatch(new ViewActions.ToggleHelp(show));
    };
    ViewService.prototype.toggleLoader = function (show) {
        show = (show == null) ? !this.store.selectSnapshot(ViewState.getLoaderActive) : show;
        this.store.dispatch(new ViewActions.ToggleLoader(show));
    };
    ViewService.prototype.toggleLabels = function (show) {
        this.store.dispatch(new ToggleLabels(show));
    };
    ViewService.prototype.toggleSearch = function (show) {
        var breakpoint = this.store.selectSnapshot(ViewState.getBreakpoint);
        var layout = this.store.selectSnapshot(ViewState.getLayout);
        show = (show == null) ? !(layout.search.status === ViewLayoutStatus.Show) : show;
        // Switch modes
        layout.search.status = show ? ViewLayoutStatus.Show : ViewLayoutStatus.Hide;
        // When in medium and below, auto hide the column as well
        if ((breakpoint === ViewBreakpoint.Medium || breakpoint === ViewBreakpoint.Small) ||
            (breakpoint === ViewBreakpoint.Large && show)) {
            this.toggleColumn(show);
        }
        if (show) {
            layout.nodeInfo.status = ViewLayoutStatus.Hide;
        }
        this.store.dispatch(new ViewActions.SetLayout(layout));
    };
    ViewService.prototype.toggleNodeInfo = function (show) {
        this.toggleInfoLayout('nodeInfo', show);
    };
    ViewService.prototype.toggleLabelInfo = function (show) {
        this.toggleInfoLayout('labelInfo', show);
    };
    ViewService.prototype.toggleInfoLayout = function (layoutField, show) {
        var layout = this.store.selectSnapshot(ViewState.getLayout);
        show = (show == null) ? layout[layoutField].status === ViewLayoutStatus.Hide : show;
        if (show) { // show target, hide others
            layout[layoutField].status = ViewLayoutStatus.Show;
            // hide others
            var infoFieldNames = ['nodeInfo', 'labelInfo'];
            infoFieldNames
                .filter(function (field) { return field !== layoutField; })
                .forEach(function (field) { return layout[field].status = ViewLayoutStatus.Hide; });
        }
        else { // hide target
            layout[layoutField].status = ViewLayoutStatus.Hide;
        }
        this.store.dispatch(new ViewActions.SetLayout(layout));
    };
    // ---------- Interaction Handlers ----------
    ViewService.prototype.delegateNodeClick = function (node, event) {
        // Always clear selected label
        this.clearSelectedLabel();
        if (!node && !event.srcEvent.shiftKey) {
            this.clearAllSelected();
        }
        else if (event.srcEvent.shiftKey || this.store.selectSnapshot(ViewState.getMultiPickModeEnabled)) {
            this.checkNode(node);
        }
        else {
            var tool = this.store.selectSnapshot(EngineState.getActiveTool);
            switch (tool) {
                case ToolEnum.Hide:
                    // this.hideNodes({nodes: [node], visible: false});
                    this.hideNode(node);
                    break;
                case ToolEnum.Select:
                    this.selectNode(node);
                    break;
                case ToolEnum.Isolate:
                    this.isolateNode(node);
                    break;
                case ToolEnum.Fade:
                    this.fadeNode(node);
                    break;
            }
        }
    };
    ViewService.prototype.clearAllSelected = function () {
        this.clearSelectedLabel();
        this.clearSelectedNodes();
    };
    ViewService.prototype.clearSelectedLabel = function () {
        this.store.dispatch(new SelectLabel());
    };
    ViewService.prototype.clearSelectedNodes = function () {
        this.store.dispatch(new SetCheckedNodes([]));
        this.store.dispatch(new SelectNode(null));
    };
    ViewService.prototype.checkNode = function (node, checked) {
        if (node === undefined) {
            return;
        }
        if (checked == null) {
            checked = this.store.selectSnapshot(EngineNodesState.getCheckedNodes).some(function (value) { return value.id === node.id; });
        }
        this.store.dispatch(new CheckNode(node, !checked));
        this.store.dispatch(new SelectNode(node));
    };
    ViewService.prototype.fadeNode = function (node) {
        this.store.dispatch(new PaintNodes([node], PaintPresetFade.recipe));
    };
    ViewService.prototype.isolateNode = function (node) {
        var viewGraph = this.store.selectSnapshot(EngineState.getViewGraph);
        this.store.dispatch(new Isolate([node], viewGraph));
    };
    ViewService.prototype.selectNode = function (node) {
        var checked = this.store.selectSnapshot(EngineNodesState.getCheckedNodes).some(function (value) { return value.id === node.id; });
        (checked) ?
            this.store.dispatch(new SetCheckedNodes([])) :
            this.store.dispatch(new SetCheckedNodes([node]));
        this.store.dispatch(new SelectNode(checked ? null : node));
        this.store.dispatch(new ExpandNode(node));
        // Pre-request node info
        this.store.dispatch(new QueryNodeInfo(node));
    };
    ViewService.prototype.hideNode = function (node) {
        var visible = false;
        this.store.dispatch(new ToggleVisibleNodes([node], visible));
        this.store.dispatch(new SetCheckedNodes([]));
        this.store.dispatch(new RecordChapterVisibilityState([node], visible, true));
        this.historyService.recordViewGraphHistory();
    };
    ViewService.prototype.selectLabel = function (label, flyTo) {
        if (flyTo === void 0) { flyTo = false; }
        var objectId = label.objectId, states = label.states;
        // select label
        this.store.dispatch(new SelectLabel(label));
        // #TODO[AC]: fly to position
        // no custom actions: fly to object
        if (flyTo && objectId && states.length === 0) {
            this.store.dispatch(new FlyToObject(objectId, true));
        }
    };
    /**
     * On a new chapter change, immediately cache the current state so that we can revert.
     * While engine has a reset() method, it currently doesn't work well with painted objects.
     * @param chapter the ChapterModel to init
     */
    ViewService.prototype.initChapter = function (chapter) {
        var viewGraph = this.engineService.createViewGraph({}, false);
        this.store.dispatch(new SetGraph(viewGraph));
        this.store.dispatch(new SetCamera(chapter.camera.location));
        this.store.dispatch(new CacheChapterSynchronization('root', chapter.id, chapter.synchronization, true));
        this.store.dispatch(new CopyChapterSynchronization('root', chapter.id, 'cache', chapter.id));
    };
    ViewService.prototype.initHistory = function () {
        var _this = this;
        var viewGraph$ = this.store.select(EngineState.getViewGraph);
        viewGraph$
            .pipe(take(1), delay(1)).subscribe(function () {
            _this.resetHistory();
        });
    };
    ViewService.prototype.resetCamera = function () {
        var chapter = this.store.selectSnapshot(StoryChaptersState.getSelectedChapter);
        if (!chapter) {
            return;
        }
        this.store.dispatch(new SetCamera(chapter.camera.location));
    };
    ViewService.prototype.resetHistory = function () {
        this.store.dispatch(new ClearHistory());
        this.historyService.recordViewGraphHistory();
    };
    ViewService.prototype.resetLayout = function () {
        var _a = this.store.selectSnapshot(ViewState), breakpoint = _a.breakpoint, layout = _a.layout, loaderActive = _a.loaderActive;
        if (loaderActive) {
            Object.keys(layout).forEach(function (key) {
                layout[key].status = ViewLayoutStatus.Hide;
            });
        }
        else {
            // Reset everything to visible
            Object.keys(layout).forEach(function (key) {
                layout[key].status = ViewLayoutStatus.Show;
            });
            // Always hidden by default
            layout.search.status = ViewLayoutStatus.Hide;
            layout.tools.status = ViewLayoutStatus.Hide;
            layout.nodeInfo.status = ViewLayoutStatus.Hide;
            layout.labelInfo.status = ViewLayoutStatus.Hide;
            // Size dependent visbility
            switch (breakpoint) {
                case ViewBreakpoint.Small:
                case ViewBreakpoint.Medium:
                    layout.tools.status = ViewLayoutStatus.Hide;
                    layout.toolbar.status = ViewLayoutStatus.Hide;
                    break;
                case ViewBreakpoint.Large:
                    layout.media.status = ViewLayoutStatus.Hide;
                    layout.menu.status = ViewLayoutStatus.Hide;
                    layout.drawer.status = ViewLayoutStatus.Hide;
                    break;
            }
            // Presentation Mode
            var presentationActive = this.store.selectSnapshot(ViewState.getPresentationActive);
            if (presentationActive) {
                layout.menu.status = ViewLayoutStatus.Hide;
                layout.column.status = ViewLayoutStatus.Hide;
                layout.drawer.status = ViewLayoutStatus.Hide;
            }
        }
        this.store.dispatch(new ViewActions.SetLayout(layout));
    };
    ViewService.prototype.undo = function () {
        this.store.dispatch(new Undo());
    };
    ViewService.prototype.toggleAnimation = function () {
        var playing = !this.store.selectSnapshot(EngineState.getAnimationPlaying);
        this.store.dispatch(new PlayAnimation(playing));
    };
    ViewService.prototype.normalizeQueryParams = function (queryParams) {
        // normalize query params
        var params = __assign({}, queryParams);
        var contentId = normalizeQueryIdParam(params);
        var contentType = normalizeQueryTypeParam(params);
        var contentLang = queryParams.lang;
        return {
            id: contentId,
            type: contentType,
            lang: contentLang
        };
    };
    // Bookmark routes use appropriate ID parameters, but internal users would like the ability to paste
    // in legacy paths. This means stripping out the .json extension at time of page load and accounting
    // for that conversion later on. 
    ViewService.prototype.sanitizeRouteId = function (id) {
        id = id.replace(/\.json$/, '');
        var query = "?id=" + id;
        this.location.replaceState('view', query);
        return id;
    };
    ViewService.prototype.openInNewTab = function () {
        var url = window.document.location.href;
        window.open(url);
    };
    // see: https://stackoverflow.com/questions/43065579/how-to-convert-css-transform-matrix-back-to-its-component-properties/43065744
    ViewService.prototype.getComputedTransform = function (element) {
        var style = getComputedStyle(element);
        var trans = style.transform;
        // ie implementation difference.
        // ie returns a 3d matrix, while others return a 2d matrix.
        // sanitize transform string value
        var values = trans
            .replace('matrix3d', '')
            .replace('matrix', '')
            .replace('(', '')
            .replace(')', '')
            .split(',')
            .map(function (s) { return s.trim(); })
            .map(function (s) { return parseInt(s, 10); });
        var computedTranslateX = values.length === 16 ? values[12] : values[4];
        var computedTranslateY = values.length === 16 ? values[13] : values[5];
        if (!Number.isNaN(computedTranslateX) && !Number.isNaN(computedTranslateY)) {
            return {
                x: computedTranslateX,
                y: computedTranslateY
            };
        }
        else {
            return null;
        }
    };
    ViewService.prototype.inferDrawerMode = function (layout) {
        // Default mode is content
        var mode = ViewMode.Content;
        if (layout.nodeInfo.status === ViewLayoutStatus.Show) {
            mode = ViewMode.NodeInfo;
        }
        else if (layout.labelInfo.status === ViewLayoutStatus.Show) {
            mode = ViewMode.LabelInfo;
        }
        else if (layout.tools.status === ViewLayoutStatus.Show) {
            mode = ViewMode.Tools;
        }
        return mode;
    };
    ViewService.ngInjectableDef = i0.ɵɵdefineInjectable({ factory: function ViewService_Factory() { return new ViewService(i0.ɵɵinject(i1.Store), i0.ɵɵinject(i2.HistoryService), i0.ɵɵinject(i3.Location), i0.ɵɵinject(i4.EngineService)); }, token: ViewService, providedIn: "root" });
    return ViewService;
}());
export { ViewService };
